/**
 * The social domain's isomorphic face (ADR 0014).
 *
 * Handles, identities, links, trust, share grants, share tokens, the relay queue and inbox items —
 * the Supabase records around a shared document. The shared Automerge document itself is
 * `document/share-doc.ts`; social owns the records, not the CRDT.
 *
 * **It never re-exports a `server/` module,** and the build is the check rather than the rule.
 * Measured on #364: this face re-exporting a plain module under `server/` fails `pnpm build` with
 * `next/headers` traced into the browser graph, while the same face re-exporting a `'use server'`
 * module passes — an action is replaced by a reference stub in the client graph. Only a plain
 * server module poisons a face.
 *
 * **It publishes no `actions.ts` export either, and that is R6 rather than taste.** Every one of the
 * ten is called from inside this domain and from nowhere else, and publishing them is not free:
 * a *server* component importing this face is a server-side import, which registers an action id
 * for **every** export of every `'use server'` module it reaches, referenced or not. Measured on
 * #403 against `app/app/settings/page.tsx`: a face publishing the four action modules took the
 * manifest to 16 node ids and made `shareEntry` and `createShareToken` network-callable, where
 * before this step they were in no build graph at all. The face publishes components and types, so
 * the RPC surface stays what R6 says it should be.
 *
 * **Nothing from `sharing/` is published**, for the same reason read the other way: both dialogs are
 * unreachable today, and a face edge is the one thing that would put them back in a build graph.
 * #47 owns whether sharing ships; this step places the code without deciding it.
 *
 * The ten exports no client calls are on `@/social/server`, and only the two public routes name it.
 *
 * **Explicit named exports, never `export *`** (ADR 0014:81-82).
 */

export { InboxProvider, useInbox } from './inbox/InboxProvider'
export { InboxList } from './inbox/inbox-list'
export type { InboxItem } from './inbox/actions'

export { LinkPersonDialog } from './links/link-person-dialog'
export { TrustToggle } from './links/trust-toggle'

export { ProfileForm } from './identity/profile-form'
